// Pure timing for TTS playback, independent of WebAudio (times are in seconds on any clock:
// AudioContext.currentTime for real audio, performance.now()/1000 for a virtual clock).
//
// Ported from live-voice-ai's playback scheduler, with one design change. Its replies were
// numbered in voicing order, so "a smaller id is stale" was true. In the kit, ids are
// process-unique but NOT in voicing order: an unprompted push gets its id when the brain opens
// it, and a user turn that arrives while the push waits goes first, so a push with a smaller id
// can be voiced after a reply with a larger one. So a reply is stale only if it was silenced or
// superseded, never because of its number. The kit voices one reply at a time, so audio for a
// new reply means the previous one's voice is over.

import type { Id } from "./protocol";

interface Segment {
  seg: number;
  start: number;
  end: number;
}

export interface Scheduled {
  /** This segment's handle (to stop it later). */
  seg: number;
  startAt: number;
  duration: number;
  /** Segments of the previous reply to stop now. */
  superseded: number[];
  /** Paused: nothing was scheduled; the audio is kept for {@link PlaybackScheduler.resume}. */
  deferred?: boolean;
}

export interface Position {
  replyId: Id;
  msPlayed: number;
}

/** How many retired reply ids to remember (old ones can no longer get audio anyway). */
const RETIRED_MAX = 512;

export class PlaybackScheduler {
  private segs: Segment[] = [];
  private current: Id | null = null;
  /** Seconds of the current reply played before its segments now queued (after a pause). */
  private base = 0;
  /** Seconds of the current reply queued in all (played or not). */
  private total = 0;
  /** The current reply is paused (protocol 1.9): its place is held, nothing plays. */
  private pausedAt: number | null = null;
  /**
   * A reply paused before any of its audio arrived (the kit paused it in a silent gap, issue
   * #18): its first audio starts out paused.
   */
  private held: Id | null = null;
  private readonly retired = new Set<Id>();
  private nextStart = 0;
  private nextSeg = 1;
  private doneReported = true;

  /** `lead`: seconds of headroom before the first segment, so it never starts in the past. */
  constructor(private readonly lead = 0.05) {}

  get currentReply(): Id | null {
    return this.current;
  }

  /** Has this reply been silenced or superseded? Its audio is dropped. */
  isRetired(replyId: Id): boolean {
    return this.retired.has(replyId);
  }

  /** The paused reply, if playback is paused. */
  get pausedReply(): Id | null {
    return this.pausedAt === null ? this.held : this.current;
  }

  /**
   * Queue `seconds` of audio for `replyId`. `null`: drop it (a silenced or superseded reply).
   * While the reply is paused it is only counted (`deferred`): it plays after {@link resume}.
   */
  enqueue(replyId: Id, seconds: number, now: number): Scheduled | null {
    if (this.retired.has(replyId)) return null;
    let superseded: number[] = [];
    if (replyId !== this.current) {
      superseded = this.segs.map((s) => s.seg);
      this.segs = [];
      if (this.current !== null) this.retire(this.current);
      this.current = replyId;
      this.nextStart = 0;
      this.base = 0;
      this.total = 0;
      this.pausedAt = this.held === replyId ? now : null;
      this.held = null;
    }
    this.total += seconds;
    if (this.pausedAt !== null) return { seg: 0, startAt: 0, duration: seconds, superseded, deferred: true };
    const start = Math.max(this.nextStart, now + this.lead);
    const seg = { seg: this.nextSeg++, start, end: start + seconds };
    this.segs.push(seg);
    this.nextStart = seg.end;
    this.doneReported = false;
    return { seg: seg.seg, startAt: start, duration: seconds, superseded };
  }

  /** The reply was silenced: its queued audio stops and later frames are dropped. */
  interrupt(replyId: Id): number[] {
    this.retire(replyId);
    if (this.held === replyId) this.held = null;
    if (replyId !== this.current) return [];
    const stop = this.segs.map((s) => s.seg);
    this.segs = [];
    this.pausedAt = null;
    this.doneReported = true;
    return stop;
  }

  /**
   * Pause the current reply where it is (`replyId`: only if it is that one). Its place and
   * everything queued are kept. Returns the segments to stop, or `null` if nothing was paused.
   */
  pause(now: number, replyId?: Id): { stop: number[]; replyId: Id; playedS: number } | null {
    // A reply with no audio here yet (paused in the gap before its first chunk): held, so its
    // audio queues when it comes.
    if (replyId !== undefined && replyId !== this.current && !this.retired.has(replyId)) {
      if (this.held === replyId) return null;
      this.held = replyId;
      return { stop: [], replyId, playedS: 0 };
    }
    if (this.current === null || this.retired.has(this.current) || this.pausedAt !== null) return null;
    if (replyId !== undefined && replyId !== this.current) return null;
    const played = this.base + this.msPlayed(now) / 1000;
    const stop = this.segs.map((s) => s.seg);
    this.segs = [];
    this.base = Math.min(played, this.total);
    this.nextStart = 0;
    this.pausedAt = now;
    return { stop, replyId: this.current, playedS: this.base };
  }

  /**
   * Carry on from the pause: the rest of the reply (from `fromS` seconds in, `durationS` long)
   * as one segment, or `null` if nothing is paused.
   */
  resume(now: number, replyId?: Id): (Scheduled & { fromS: number }) | null {
    if (this.held !== null && (replyId === undefined || replyId === this.held)) {
      // Nothing of it had arrived: nothing to play yet, and its audio plays as it comes.
      this.held = null;
      return null;
    }
    if (this.current === null || this.pausedAt === null || (replyId !== undefined && replyId !== this.current)) return null;
    this.pausedAt = null;
    const rest = Math.max(0, this.total - this.base);
    const start = now + this.lead;
    const seg = { seg: this.nextSeg++, start, end: start + rest };
    this.segs = [seg];
    this.nextStart = seg.end;
    this.doneReported = false;
    return { seg: seg.seg, startAt: start, duration: rest, superseded: [], fromS: this.base };
  }

  /** Stop whatever is playing (session end). Returns segments to stop. */
  stopAll(): number[] {
    return this.current === null ? [] : this.interrupt(this.current);
  }

  /** Forget everything, retired ids included (a new kit process: ids start over). */
  reset(): number[] {
    const stop = this.segs.map((s) => s.seg);
    this.segs = [];
    this.current = null;
    this.retired.clear();
    this.held = null;
    this.nextStart = 0;
    this.base = 0;
    this.total = 0;
    this.pausedAt = null;
    this.doneReported = true;
    return stop;
  }

  /**
   * The clock jumped by `delta` seconds (the audio moved to a new AudioContext, whose clock
   * starts at 0): every time kept moves with it, so nothing waits on the old clock.
   */
  rebase(delta: number): void {
    if (!delta) return;
    for (const s of this.segs) {
      s.start += delta;
      s.end += delta;
    }
    if (this.nextStart) this.nextStart = Math.max(0, this.nextStart + delta);
  }

  /** Playback position of the current reply, or null when nothing is queued. */
  progress(now: number): Position | null {
    if (this.current === null || (!this.segs.length && this.pausedAt === null)) return null;
    return { replyId: this.current, msPlayed: Math.round(this.base * 1000) + this.msPlayed(now) };
  }

  /** Once per drained queue: everything queued has played. Never while paused. */
  pollDone(now: number): Position | null {
    const last = this.segs[this.segs.length - 1];
    if (!last || this.doneReported || now < last.end || this.current === null || this.pausedAt !== null) return null;
    this.doneReported = true;
    return { replyId: this.current, msPlayed: Math.round(this.base * 1000) + this.msPlayed(now) };
  }

  isPlaying(now: number): boolean {
    return this.pausedAt === null && this.segs.some((s) => now < s.end);
  }

  /** Ms played of the segments queued since the last pause. */
  private msPlayed(now: number): number {
    let s = 0;
    for (const seg of this.segs) s += Math.min(Math.max(now - seg.start, 0), seg.end - seg.start);
    return Math.round(s * 1000);
  }

  private retire(id: Id): void {
    this.retired.add(id);
    if (this.retired.size > RETIRED_MAX) {
      const oldest = this.retired.values().next().value;
      if (oldest !== undefined) this.retired.delete(oldest);
    }
  }
}
